/**
 * Global number input sanitizer.
 * 
 * Fixes the common React / HTML5 issue where typing into a number field
 * that has a default "0" results in leading zeros (e.g., "090" instead of "90").
 * 
 * 1. Strips leading zeros when digits are typed (e.g. "09" -> "9", "090" -> "90").
 * 2. Preserves valid single zero ("0") and decimals ("0.5", "0.05").
 * 3. Auto-selects text on focus when value is "0" so typing immediately overwrites it.
 */
export function setupGlobalNumberInputSanitizer(): void {
  if (typeof window === 'undefined') return;

  const sanitizeNumberInput = (target: HTMLInputElement) => {
    if (target.type !== 'number') return;
    const val = target.value;
    if (val && val.length > 1 && /^0\d/.test(val)) {
      const cleaned = val.replace(/^0+(?=\d)/, '');
      if (cleaned !== val) {
        const nativeSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;
        if (nativeSetter) {
          nativeSetter.call(target, cleaned);
        } else {
          target.value = cleaned;
        }
        target.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  };

  // Capture phase on input to sanitize before React / Form state handlers process it
  document.addEventListener(
    'input',
    (e) => {
      const target = e.target as HTMLInputElement | null;
      if (target && target.tagName === 'INPUT') {
        sanitizeNumberInput(target);
      }
    },
    true
  );

  // Auto-select on focus if value is "0"
  document.addEventListener(
    'focusin',
    (e) => {
      const target = e.target as HTMLInputElement | null;
      if (target && target.tagName === 'INPUT' && target.type === 'number') {
        if (target.value === '0') {
          setTimeout(() => {
            try {
              target.select();
            } catch {
              // Ignore if select is not supported
            }
          }, 10);
        }
      }
    },
    true
  );
}
